import React, { Component, useContext } from 'react'
import AuthenticatedPage from '../../../containers/AuthenticatedPage/index'
import StoreSelector, {
  makestoreDependentComponent,
  getDefaultStore,
} from '../../../containers/StoreSelector'
import { SplitContext } from '../../../containers/SplitContext'
import API from '../../../lib/api'
import { getMessage } from '../../../lib/translator'
import { isExtensionEnabled, getSapStoreId } from '../../../lib/auth'
import { get } from '../../../lib/storage'
import Loader from '../../../components/Loader'
import Toggle from '../../../components/Form/Inputs/Toggle'

import './style.css'
import { sortOrderStatus, sortPacklistStatus } from '../../../lib/commonlyused'
import {
  ORDER_DETAIL_COUNT_URL,
  PACKLIST_DETAIL_COUNT_URL,
  isConsolidatedCountsEnabled,
  buildOrderCountParams,
  buildPacklistCountParams,
  adaptOrderCountResponse,
  adaptPacklistCountResponse,
} from './consolidatedCounts'
import { diffChangedCells, hasChangedCells } from './cellDiff'
import DashboardHeader from './DashboardHeader'
import OrderPipeline from './OrderPipeline'
import CountTable from './CountTable'
import SlotOrderTable from './slotOrdertable'
import ZonesDropdown from './ZonesDropdown'

// Formats Date object in YYYY-MM-DD format
function formatDate(date) {
  var dd = date.getDate()
  var mm = date.getMonth() + 1 // January is 0!

  var yyyy = date.getFullYear()
  if (dd < 10) {
    dd = '0' + dd
  }
  if (mm < 10) {
    mm = '0' + mm
  }
  return `${yyyy}-${mm}-${dd}`
}

/**
 * Sums per-slot counts into one map keyed by status. Both the total row and the
 * pipeline tiles read from this, so they can never disagree with each other.
 */
export const sumByStatus = (slots, statuses) => {
  const total = {}
  ;(statuses || []).forEach((status) => {
    total[status] = 0
  })
  ;(slots || []).forEach((entry) => {
    Object.keys(entry.count || {}).forEach((status) => {
      total[status] = (total[status] || 0) + (Number(entry.count[status]) || 0)
    })
  })
  return total
}

// Long enough to cover the last staggered cell: CountTable delays a flash by up to
// FLASH_MAX_DELAY_MS (900) and od-flash itself runs 1100ms. Drop below that and the
// tail of the table loses its highlight part-way through.
const FLASH_DURATION = 2100

const ErrorIcon = () => (
  <svg
    className="od-icon"
    width="28"
    height="28"
    viewBox="0 0 24 24"
    fill="none"
    aria-hidden="true"
  >
    <path
      d="M12 9v4M12 17h.01M10.3 3.86l-8 14A2 2 0 004 21h16a2 2 0 001.7-3.14l-8-14a2 2 0 00-3.4 0z"
      stroke="#c10b3a"
      strokeWidth="1.9"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
)

class Dashboard extends Component {
  constructor(props) {
    super(props)
    this.state = {
      showSlotLoader: true,
      loadError: false,
      storeId: get('store') || getDefaultStore(this.props.stores).storeId,
      showStore: false,
      selectedDate: formatDate(new Date()),
      storeZone: 'none',
      // Amended (POA) orders are top-ups on an existing order, so by default they are
      // not counted as orders of their own.
      excludeAmended: true,
    }
    this.changeStore = this.changeStore.bind(this)
    this.handleDateChange = this.handleDateChange.bind(this)
    this.handleStoreZoneChange = this.handleStoreZoneChange.bind(this)
    this.handleExcludeAmendedChange = this.handleExcludeAmendedChange.bind(this)
    this.handleRetry = this.handleRetry.bind(this)
    this.handleLoadError = this.handleLoadError.bind(this)
    this.handleVisibilityChange = this.handleVisibilityChange.bind(this)
  }

  // Ops staff leave this open in a background tab, so the counts on screen are as
  // old as the last time they looked. Refetch when they come back to it.
  handleVisibilityChange() {
    if (document.visibilityState === 'visible') {
      this.updateContent()
    }
  }

  handleDateChange(date) {
    this.setState({ selectedDate: date }, () => this.updateContent())
  }

  changeStore(storeId) {
    this.setState(
      {
        storeId: storeId,
        storeZone: 'none',
      },
      () => this.updateContent()
    )
  }

  handleStoreZoneChange(storeZone) {
    this.setState({ storeZone }, () => {
      const storeId = this.resolveStoreId()
      if (this.useConsolidatedCounts()) {
        this.fetchPacklistDetailCounts(storeId)
        return
      }
      this.fetchLegacyPacklistCounts(storeId)
    })
  }

  handleExcludeAmendedChange(excludeAmended) {
    this.setState({ excludeAmended }, () => {
      this.fetchOrderDetailCounts(this.resolveStoreId()).catch(
        this.handleLoadError
      )
    })
  }

  handleRetry() {
    this.updateContent()
  }

  handleLoadError(error) {
    // componentWillUnmount cancels in-flight requests; that is not a load failure.
    if (error && error.message === 'cancelled') {
      return
    }
    console.error('error loading operations dashboard: ', error)
    this.setState({ loadError: true, showSlotLoader: false })
  }

  componentDidMount() {
    this.updateContent()
    document.addEventListener('visibilitychange', this.handleVisibilityChange)
  }

  componentDidUpdate(prevProps) {
    if (prevProps.splits === this.props.splits || this.props.splitsLoading) {
      return
    }
    const wasConsolidated = isConsolidatedCountsEnabled(prevProps.splits)
    const isConsolidated = isConsolidatedCountsEnabled(this.props.splits)
    if (wasConsolidated !== isConsolidated) {
      this.updateContent()
    }
  }

  useConsolidatedCounts() {
    return isConsolidatedCountsEnabled(this.props.splits)
  }

  resolveStoreId() {
    const storeId =
      this.state.storeId ||
      get('store') ||
      getDefaultStore(this.props.stores).storeId
    const org = JSON.parse(get('organization'))
    return storeId || org.defaultStoreId
  }

  applyStoreZones(storeZoneResponse) {
    const storeZoneConfig =
      (storeZoneResponse && storeZoneResponse.storeZones) || []
    const allZones = { id: '0', tag: 'none', displayName: 'All zones' }
    if (!storeZoneConfig.length) {
      this.setState({
        showZonesDropdown: false,
        storeZoneConfig: { 0: allZones },
      })
      return false
    }
    this.setState({
      showZonesDropdown: true,
      storeZoneConfig: [...storeZoneConfig, allZones],
    })
    return true
  }

  fetchOrderDetailCounts(storeId) {
    const areOrdersScheduled = isExtensionEnabled('DeliverySlots')
    const { excludeAmended } = this.state
    this.orderDetailCountsApi = new API({ url: ORDER_DETAIL_COUNT_URL })

    return this.orderDetailCountsApi
      .get(
        buildOrderCountParams({
          storeId,
          sapStoreId: getSapStoreId(storeId),
          preferredDate: this.state.selectedDate,
          excludePoa: excludeAmended,
        })
      )
      .then((response) => {
        // Flipping the toggle twice quickly can land the older response last; drop it so
        // the counts never disagree with the toggle.
        if (excludeAmended !== this.state.excludeAmended) {
          return
        }
        const orders = adaptOrderCountResponse(response, { areOrdersScheduled })
        this.applyCounts(orders, null, storeId)
      })
  }

  fetchPacklistDetailCounts(storeId) {
    this.packlistDetailCountsApi = new API({ url: PACKLIST_DETAIL_COUNT_URL })

    return this.packlistDetailCountsApi
      .get(
        buildPacklistCountParams({
          storeId,
          sapStoreId: getSapStoreId(storeId),
          preferredDate: this.state.selectedDate,
          storeZone: this.state.storeZone,
        })
      )
      .then((response) => {
        const packlists = adaptPacklistCountResponse(response)
        this.applyCounts(null, packlists, storeId)
      })
  }

  applyCounts(orders, packlists, storeId) {
    this.setState((prevState) => {
      const stateUpdate = { storeId }
      const changedCells = {
        orders:
          prevState.changedCells && prevState.changedCells.orders
            ? prevState.changedCells.orders
            : null,
        packlists:
          prevState.changedCells && prevState.changedCells.packlists
            ? prevState.changedCells.packlists
            : null,
      }

      if (orders) {
        const orderQueryKey = `${storeId}|${this.state.selectedDate}|${this.state.excludeAmended}`
        const isOrderRefresh = this.lastOrderQueryKey === orderQueryKey
        this.lastOrderQueryKey = orderQueryKey
        changedCells.orders = isOrderRefresh
          ? diffChangedCells(prevState.orderSlots, orders.orderSlots)
          : null
        Object.assign(stateUpdate, orders)
      }

      if (packlists) {
        const packlistQueryKey = `${storeId}|${this.state.selectedDate}|${this.state.storeZone}`
        const isPacklistRefresh = this.lastPacklistQueryKey === packlistQueryKey
        this.lastPacklistQueryKey = packlistQueryKey
        changedCells.packlists = isPacklistRefresh
          ? diffChangedCells(prevState.packlistSlots, packlists.packlistSlots)
          : null
        Object.assign(stateUpdate, packlists)
      }

      const hasAnyChangedCells =
        (changedCells.orders && Object.keys(changedCells.orders).length > 0) ||
        (changedCells.packlists &&
          Object.keys(changedCells.packlists).length > 0)
      const changedCellsOrNull = hasAnyChangedCells ? changedCells : null

      if (
        changedCellsOrNull &&
        hasChangedCells(changedCellsOrNull.orders, changedCellsOrNull.packlists)
      ) {
        this.scheduleFlashClear()
      }

      return {
        ...stateUpdate,
        changedCells: changedCellsOrNull,
      }
    })
  }

  scheduleFlashClear() {
    this.clearFlashTimer()
    this.flashTimer = setTimeout(
      () => this.setState({ changedCells: null }),
      FLASH_DURATION
    )
  }

  clearFlashTimer() {
    if (this.flashTimer) {
      clearTimeout(this.flashTimer)
      this.flashTimer = null
    }
  }

  fetchLegacyPacklistCounts(storeId) {
    const { storeZone, selectedDate } = this.state
    const params =
      storeZone === 'none'
        ? { preferredDate: selectedDate, storeId }
        : { preferredDate: selectedDate, storeId, tag: storeZone }

    return this.slotPacklistsApi
      .get(params)
      .then((slotPacklistsResponse) => {
        const slotPacklistsStats = slotPacklistsResponse.packlistCount || []
        let totalPacklists = 0
        const packlistFixedSlots = []
        const packlistStatuses = []
        slotPacklistsStats.forEach((packlistCount) => {
          const statusCount = {}
          packlistCount.countByStatus.forEach((countByStatus) => {
            const key = countByStatus.status
            const value = parseInt(countByStatus.count, 10) || 0
            if (packlistStatuses.indexOf(key) === -1) {
              packlistStatuses.push(key)
            }
            totalPacklists += value
            statusCount[key] = value
          })
          packlistFixedSlots.push({
            count: statusCount,
            slot: {
              startTime: packlistCount.slot.slotStartTime,
              endTime: packlistCount.slot.slotEndTime,
            },
          })
        })
        this.setState({
          totalPacklists,
          packlistStatuses: sortPacklistStatus(packlistStatuses),
          packlistFixedSlots,
        })
      })
      .catch(this.handleLoadError)
  }

  fetchLegacyOrderCounts(storeId) {
    const areOrdersScheduled = isExtensionEnabled('DeliverySlots')
    const { selectedDate } = this.state
    const params = areOrdersScheduled
      ? { preferredDate: selectedDate, storeId }
      : { createdAt: selectedDate, storeId }

    return this.slotOrdersApi
      .get(params)
      .then((slotOrdersResponse) => {
        const slotOrderStats =
          (slotOrdersResponse && slotOrdersResponse.data) || []
        const statuses = []
        let totalOrders = 0
        slotOrderStats.forEach((slot) => {
          const keys = (slot.count && Object.keys(slot.count)) || []
          keys.forEach((key) => {
            if (statuses.indexOf(key) === -1) {
              statuses.push(key)
            }
            totalOrders += slot.count[key]
          })
        })

        let fixedSlots = null
        let asapSlots = null
        let noSlots = null
        if (areOrdersScheduled) {
          // SlotOrderTable expects the raw order-service slot objects (type + times).
          fixedSlots = slotOrderStats
            .filter((slot) => slot.slot?.type === 'STANDARD')
            .sort((a, b) => (a.slot.startTime < b.slot.startTime ? -1 : 1))
          asapSlots = slotOrderStats
            .filter((slot) => slot.slot && slot.slot.type === 'ASAP')
            .sort((a, b) => (a.slot.endTime < b.slot.endTime ? -1 : 1))
        } else {
          const count = {}
          slotOrderStats.forEach((slot) => {
            Object.keys(slot.count || {}).forEach((key) => {
              count[key] = (count[key] || 0) + slot.count[key]
            })
          })
          noSlots = { count }
        }

        this.setState({
          statuses: sortOrderStatus(statuses),
          totalOrders,
          fixedSlots,
          asapSlots,
          noSlots,
          showSlotLoader: false,
          storeId,
        })
      })
      .catch(this.handleLoadError)
  }

  updateContent() {
    this.setState({
      showSlotLoader: true,
      loadError: false,
      showStore: true,
    })
    const storeId = this.resolveStoreId()
    if (!storeId) {
      return
    }

    this.storeZoneApi = new API({
      url: '/picking-service/v3/storeZones/' + storeId,
    })

    if (this.useConsolidatedCounts()) {
      // The zone config only depends on the store, so a date or zone change refetches
      // nothing but the counts. Holding the loader until both settle stops the packlist
      // section popping in late on first load.
      const zonesLoaded =
        this.zonesLoadedForStore === storeId
          ? Promise.resolve()
          : this.storeZoneApi.get().then((response) => {
              this.zonesLoadedForStore = storeId
              this.applyStoreZones(response)
            })

      Promise.all([
        zonesLoaded,
        this.fetchOrderDetailCounts(storeId),
        this.fetchPacklistDetailCounts(storeId),
      ])
        .then(() => this.setState({ showSlotLoader: false }))
        .catch(this.handleLoadError)
      return
    }

    this.slotOrdersApi = new API({ url: '/order-service/order-count' })
    this.slotPacklistsApi = new API({
      url: '/picking-service/v3/packlists/count',
    })

    this.storeZoneApi
      .get()
      .then((response) => {
        if (this.applyStoreZones(response) && this.state.storeZone) {
          return this.fetchLegacyPacklistCounts(storeId)
        }
        return null
      })
      .catch(this.handleLoadError)

    this.fetchLegacyOrderCounts(storeId)
  }

  componentWillUnmount() {
    document.removeEventListener(
      'visibilitychange',
      this.handleVisibilityChange
    )
    this.clearFlashTimer()
    this.slotOrdersApi && this.slotOrdersApi.cancel()
    this.storeZoneApi && this.storeZoneApi.cancel()
    this.slotPacklistsApi && this.slotPacklistsApi.cancel()
    this.orderDetailCountsApi && this.orderDetailCountsApi.cancel()
    this.packlistDetailCountsApi && this.packlistDetailCountsApi.cancel()
  }

  renderError() {
    return (
      <div className="od-card od-error" data-testid="dashboard-error">
        <div className="od-error-icon">
          <ErrorIcon />
        </div>
        <h2 className="od-error-title">
          {getMessage('operations.dashboard.error.heading')}
        </h2>
        <p className="od-error-message">
          {getMessage('operations.dashboard.error.message')}
        </p>
        <button
          type="button"
          className="od-primary-button"
          onClick={this.handleRetry}
          data-testid="dashboard-retry"
        >
          {getMessage('operations.dashboard.error.retry')}
        </button>
      </div>
    )
  }

  renderLegacyContent() {
    const {
      showSlotLoader,
      statuses,
      asapSlots,
      totalOrders,
      fixedSlots,
      noSlots,
      showZonesDropdown,
      storeZone,
      totalPacklists,
      packlistStatuses,
      packlistFixedSlots,
    } = this.state

    if (showSlotLoader) {
      return <Loader />
    }

    const areOrdersScheduled = isExtensionEnabled('DeliverySlots')
    const ordersTable = (
      <SlotOrderTable
        data={{ totalOrders, asapSlots, fixedSlots, noSlots, statuses }}
        handleDateChange={this.handleDateChange}
        selectedDate={this.state.selectedDate}
        areOrdersScheduled={areOrdersScheduled}
      />
    )

    if (!showZonesDropdown) {
      return ordersTable
    }

    return (
      <div>
        {ordersTable}
        <SlotOrderTable
          data={{
            totalPacklists,
            asapSlots,
            packlistFixedSlots,
            noSlots,
            packlistStatuses,
          }}
          handleDateChange={this.handleDateChange}
          handleStoreZoneChange={this.handleStoreZoneChange}
          showZonesDropdown={showZonesDropdown}
          storeZoneConfig={this.state.storeZoneConfig}
          storeZone={storeZone}
          selectedDate={this.state.selectedDate}
          areOrdersScheduled={areOrdersScheduled}
        />
      </div>
    )
  }

  renderContent() {
    const {
      statuses,
      totalOrders,
      orderSlots,
      noSlots,
      showZonesDropdown,
      storeZone,
      totalPacklists,
      packlistStatuses,
      packlistSlots,
      selectedDate,
      showSlotLoader,
      changedCells,
      excludeAmended,
    } = this.state

    const ordersLoading = showSlotLoader && !(statuses && statuses.length)
    const packlistsLoading =
      showSlotLoader && !(packlistStatuses && packlistStatuses.length)

    // Orgs without delivery slots get a single aggregate row rather than a slot list.
    const orderRows =
      orderSlots ||
      (noSlots
        ? [
            {
              count: noSlots.count,
              slot: {},
              label: getMessage('operations.dashboard.counts'),
            },
          ]
        : [])

    const orderTotal = sumByStatus(orderRows, statuses)
    const packlistTotal = sumByStatus(packlistSlots, packlistStatuses)

    return (
      <>
        <OrderPipeline
          statuses={statuses}
          total={orderTotal}
          grandTotal={totalOrders}
          isLoading={ordersLoading}
        />
        <CountTable
          variant="orders"
          title={getMessage('operations.dashboard.ordersHeading')}
          slots={orderRows}
          statuses={statuses}
          total={orderTotal}
          grandTotal={totalOrders}
          selectedDate={selectedDate}
          usePickingLinks
          dateParam="preferredDate"
          changedCells={changedCells && changedCells.orders}
          isLoading={ordersLoading}
          headerControls={
            <Toggle
              className="od-exclude-amended"
              name="exclude-amended-orders"
              togglelabel={getMessage('operations.dashboard.excludeAmended')}
              value={excludeAmended}
              icons={false}
              onChange={this.handleExcludeAmendedChange}
            />
          }
        />
        {showZonesDropdown && (
          <CountTable
            variant="packlists"
            title={getMessage('operations.dashboard.packlistHeading')}
            slots={packlistSlots}
            statuses={packlistStatuses}
            total={packlistTotal}
            grandTotal={totalPacklists}
            selectedDate={selectedDate}
            storeZone={storeZone}
            usePickingLinks
            changedCells={changedCells && changedCells.packlists}
            isLoading={packlistsLoading}
            headerControls={
              <ZonesDropdown
                onChange={this.handleStoreZoneChange}
                storeZoneConfig={this.state.storeZoneConfig}
                isZoneError={false}
                value={storeZone}
                disabled={false}
              />
            }
          />
        )}
      </>
    )
  }

  render() {
    const { props } = this
    const { showSlotLoader, loadError, showStore } = this.state
    const areOrdersScheduled = isExtensionEnabled('DeliverySlots')
    const useNewDashboard = this.useConsolidatedCounts()

    return (
      <div className="operations-dashboard">
        {showStore && isExtensionEnabled('MultiStoreSupport') && (
          <StoreSelector
            value={this.state.storeId}
            onChange={this.changeStore}
            stores={props.stores}
          />
        )}
        {useNewDashboard ? (
          <>
            <DashboardHeader
              isToday={this.state.selectedDate === formatDate(new Date())}
              selectedDate={this.state.selectedDate}
              onDateChange={this.handleDateChange}
              onRefresh={this.handleRetry}
              areOrdersScheduled={areOrdersScheduled}
              isLoading={showSlotLoader}
            />
            {loadError ? this.renderError() : this.renderContent()}
          </>
        ) : (
          <>
            <h1 className="title">
              {getMessage('operations.dashboard.heading')}
            </h1>
            <div>
              {loadError ? this.renderError() : this.renderLegacyContent()}
            </div>
          </>
        )}
      </div>
    )
  }
}

const DashboardWithStores = makestoreDependentComponent(Dashboard)

const DashboardWithStoresAndSplits = (props) => {
  const { splits, loading: splitsLoading } = useContext(SplitContext)
  return (
    <DashboardWithStores
      {...props}
      splits={splits}
      splitsLoading={splitsLoading}
    />
  )
}

// Exported so tests can exercise the fetch logic without the authenticated page chrome.
export { Dashboard, DashboardWithStores }

export default class OperationsDashboard extends Component {
  render() {
    return (
      <AuthenticatedPage menu={this.props.menu}>
        <DashboardWithStoresAndSplits {...this.props} />
      </AuthenticatedPage>
    )
  }
}
